import { useState } from 'react'
import {
  apiUserDupCheck,
  apiUserRegister,
  apiUserLogin,
} from '../server/ServiceCall'
import { AuthState, auth, parseLoginData } from '../store/auth'
import '../css/App.css'

interface Props {
  onLoggedIn: (a: AuthState) => void
}

type Mode = 'login' | 'register'
type DupState = 'none' | 'ok' | 'dup' | 'checking'

export default function Login({ onLoggedIn }: Props) {
  const [mode,     setMode]     = useState<Mode>('login')
  const [userId,   setUserId]   = useState('')
  const [pass,     setPass]     = useState('')
  const [pass2,    setPass2]    = useState('')
  const [nickname, setNickname] = useState('')
  const [busy,     setBusy]     = useState(false)
  const [msg,      setMsg]      = useState('')
  const [msgOk,    setMsgOk]    = useState(false)
  const [dupState, setDupState] = useState<DupState>('none')

  function switchMode(m: Mode) {
    setMode(m); setMsg(''); setMsgOk(false); setDupState('none')
    setUserId(''); setPass(''); setPass2(''); setNickname('')
  }

  function showErr(text: string) { setMsg(text); setMsgOk(false) }
  function showOk(text: string)  { setMsg(text); setMsgOk(true)  }

  // ── 중복 확인 ──────────────────────────────────────────
  async function handleDupCheck() {
    if (!userId.trim()) { showErr('ID를 입력하세요.'); return }
    if (userId.trim().length < 4) { showErr('ID는 4자 이상이어야 합니다.'); return }
    setDupState('checking')
    try {
      const r = await apiUserDupCheck(userId.trim())
      if (r.resultCode !== '00') { showErr(r.resultMessage || '중복 확인 실패'); setDupState('none'); return }
      // 서버: resultData "0" = 사용 가능, "1" = 중복 (설계 §4-1)
      if (r.resultData === '1') {
        setDupState('dup'); showErr('이미 사용 중인 ID입니다.')
      } else {
        setDupState('ok'); showOk('사용 가능한 ID입니다.')
      }
    } catch (err: any) {
      showErr(err?.message || '네트워크 오류')
      setDupState('none')
    }
  }

  // ── 로그인 ─────────────────────────────────────────────
  async function handleLogin(e: React.FormEvent) {
    e.preventDefault()
    if (!userId.trim()) { showErr('ID를 입력하세요.'); return }
    if (!pass)          { showErr('비밀번호를 입력하세요.'); return }
    setBusy(true); setMsg(''); setMsgOk(false)
    try {
      // 평문 전송 — 서버에서 SHA256 처리 (WebFm 패턴 일치)
      const r = await apiUserLogin(userId.trim(), pass)
      if (r.resultCode !== '00') { showErr(r.resultMessage || '로그인 실패'); return }
      const a = parseLoginData(r.resultData)
      if (!a) { showErr('서버 응답 형식 오류'); return }
      auth.set(a)
      onLoggedIn(a)
    } catch (err: any) {
      showErr(err?.message || '네트워크 오류')
    } finally {
      setBusy(false)
    }
  }

  // ── 회원가입 ───────────────────────────────────────────
  async function handleRegister(e: React.FormEvent) {
    e.preventDefault()
    if (!userId.trim())        { showErr('ID를 입력하세요.'); return }
    if (userId.trim().length < 4) { showErr('ID는 4자 이상이어야 합니다.'); return }
    if (pass.length < 4)       { showErr('비밀번호는 4자 이상이어야 합니다.'); return }
    if (pass !== pass2)        { showErr('비밀번호가 일치하지 않습니다.'); return }
    if (!nickname.trim())      { showErr('닉네임을 입력하세요.'); return }
    if (dupState !== 'ok')     { showErr('ID 중복 확인을 완료해 주세요.'); return }

    setBusy(true); setMsg(''); setMsgOk(false)
    try {
      // 평문 전송 — 서버에서 SHA256 처리
      const r = await apiUserRegister(userId.trim(), pass, nickname.trim())
      if (r.resultCode !== '00') { showErr(r.resultMessage || '회원가입 실패'); return }

      // 가입 성공 → 자동 로그인
      const lr = await apiUserLogin(userId.trim(), pass)
      if (lr.resultCode !== '00') { showErr('가입은 됐으나 로그인 실패: ' + lr.resultMessage); return }
      const a = parseLoginData(lr.resultData)
      if (!a) { showErr('서버 응답 형식 오류'); return }
      auth.set(a)
      onLoggedIn(a)
    } catch (err: any) {
      showErr(err?.message || '네트워크 오류')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="login-wrap">
      <div className="login-card">
        <h1>보드겜돌이</h1>
        <p className="subtitle">온라인 보드게임 플랫폼</p>

        <div className="tabs">
          <button
            className={mode === 'login' ? 'tab active' : 'tab'}
            onClick={() => switchMode('login')}
            disabled={busy}
          >로그인</button>
          <button
            className={mode === 'register' ? 'tab active' : 'tab'}
            onClick={() => switchMode('register')}
            disabled={busy}
          >회원가입</button>
        </div>

        {mode === 'login' ? (
          <form onSubmit={handleLogin} className="form">
            <input
              type="text" placeholder="ID"
              value={userId} onChange={e => setUserId(e.target.value)}
              disabled={busy} autoFocus
            />
            <input
              type="password" placeholder="비밀번호"
              value={pass} onChange={e => setPass(e.target.value)}
              disabled={busy}
            />
            <button type="submit" disabled={busy}>
              {busy && <span className="spinner" />}
              로그인
            </button>
          </form>
        ) : (
          <form onSubmit={handleRegister} className="form">
            <div className="dup-row">
              <input
                type="text" placeholder="ID (영숫자 4~32자)"
                value={userId}
                onChange={e => { setUserId(e.target.value); setDupState('none') }}
                disabled={busy} autoFocus
              />
              <button
                type="button" className="dup-btn"
                onClick={handleDupCheck}
                disabled={busy || dupState === 'checking'}
              >
                {dupState === 'checking' ? <span className="spinner" /> : '중복확인'}
              </button>
            </div>
            <input
              type="password" placeholder="비밀번호 (4자 이상)"
              value={pass} onChange={e => setPass(e.target.value)}
              disabled={busy}
            />
            <input
              type="password" placeholder="비밀번호 확인"
              value={pass2} onChange={e => setPass2(e.target.value)}
              disabled={busy}
            />
            <input
              type="text" placeholder="닉네임 (한글 가능, 1~32자)"
              value={nickname} onChange={e => setNickname(e.target.value)}
              disabled={busy}
            />
            <button type="submit" disabled={busy}>
              {busy && <span className="spinner" />}
              가입 후 시작
            </button>
          </form>
        )}

        {msg && <p className={msgOk ? 'msg ok' : 'msg'}>{msg}</p>}
        <p className="phase">Phase 0 — 인프라 골격</p>
      </div>
    </div>
  )
}
